'use client'

import { ptBR } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type React from 'react'
import { DayPicker } from 'react-day-picker'

import { cn } from '@/lib/cn'

import { buttonVariants } from '../button'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) => {
  return (
    <DayPicker
      locale={ptBR}
      showOutsideDays={showOutsideDays}
      className={cn('p-3 rounded-lg shadow-md bg-white', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-3 sm:space-x-3 sm:space-y-0',
        month: 'space-y-3 text-system-text-primar',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'secondary' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex border-b border-system-grey-support pb-3',
        head_cell: 'text-system-text-secondary w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-3',
        cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-system-primary-blue/50 [&:has([aria-selected])]:bg-white first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-system-text-primary rounded-full',
        ),
        day_range_end: 'day-range-end',
        day_selected:
          'bg-system-primary-blue text-white hover:bg-system-primary-blue hover:text-white focus:bg-system-primary-blue focus:text-white text-white',
        day_today:
          'bg-white border border-system-primary-blue text-system-text-primary',
        day_outside:
          'day-outside text-system-text-secondary opacity-50 aria-selected:bg-system-primary-blue/50 aria-selected:text-system-text-secondary aria-selected:opacity-30',
        day_disabled: 'text-system-text-secondary opacity-50',
        day_range_middle:
          'aria-selected:bg-system-primary-blue aria-selected:text-white',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ ...props }) => (
          <ChevronLeft {...props} className="size-5 text-system-text-primary" />
        ),
        IconRight: ({ ...props }) => (
          <ChevronRight
            {...props}
            className="size-5 text-system-text-primary"
          />
        ),
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
